import { ArrowUpRight, ArrowRight, ShoppingBag } from 'lucide-react';
import SectionMarker from '@/components/sections/SectionMarker';
import PageHero from '@/components/sections/PageHero';
import { Link } from 'react-router-dom';
import { RESOURCES_ITEMS, STAN_STRATEGY_URL, DIGITAL_PRODUCTS_URL, PHOTOS } from '@/constants/content';
import { RESOURCES } from '@/constants/testIds';
import { usePageMeta } from '@/lib/usePageMeta';
import { SEO } from '@/constants/seo';
import { analytics } from '@/lib/analytics';

const AMAZON_STOREFRONT_URL =
  'https://www.amazon.com/shop/iamtoiawallace/list/3EBO3QUPT3GLF?ccs_id=ed8650dc-3fce-41c4-9287-3a254440c457';

export default function Resources() {
  usePageMeta({ ...SEO.resources });

  const handleAmazonClick = () => {
    analytics.track('amazon_store_clicked', {
      source_page: 'resources',
      resource_name: 'Shop My Amazon Storefront',
      destination: 'amazon_storefront',
    });
  };

  return (
    <div className="bg-ivory">
      <section className="pt-40 lg:pt-56 pb-16 lg:pb-24">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow text-taupe mb-8">Resources · For Founders</div>
            <h1 className="font-serif-display text-6xl md:text-7xl lg:text-[8rem] leading-[0.94] tracking-tightest text-matte">
              Tools, templates,
              <br />
              <em className="italic text-taupe">and dispatches.</em>
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-matte/70 leading-relaxed text-[15px] max-w-md">
              A curated shelf of founder-tested resources, from operating templates to AI
              deployment guides, designed to help you build with intention.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Featured — Amazon Storefront (curated founder pick) */}
            <a
              href={AMAZON_STOREFRONT_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleAmazonClick}
              aria-label="Shop Toia Wallace's Amazon Storefront"
              data-testid="resource-card-amazon-storefront"
              className="group relative block bg-ivory rounded-2xl p-10 md:col-span-2 border border-transparent hover:border-gold/40 transition-[border-color,transform] duration-500 hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-white min-w-0"
            >
              {/* Curated Favorites badge */}
              <span className="absolute top-6 right-6 inline-flex items-center gap-1.5 rounded-full border border-gold/50 bg-white/70 px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-gold">
                <span className="w-1.5 h-1.5 rounded-full bg-gold" aria-hidden="true" />
                Curated Favorites
              </span>

              <div className="flex items-start justify-between mb-8">
                <span
                  className="w-12 h-12 rounded-full bg-white border border-taupe/30 grid place-items-center text-matte group-hover:text-gold group-hover:border-gold/40 transition-colors duration-500"
                  aria-hidden="true"
                >
                  <ShoppingBag size={20} strokeWidth={1.4} />
                </span>
                <ArrowUpRight
                  size={20}
                  strokeWidth={1.2}
                  className="text-matte/40 group-hover:text-gold transition-colors duration-500 mt-2"
                  aria-hidden="true"
                />
              </div>

              <h3 className="font-serif-display text-3xl md:text-4xl tracking-tighter text-matte leading-tight mb-3">
                Shop My Amazon Storefront
              </h3>
              <p className="text-taupe italic font-serif-display text-xl md:text-2xl leading-snug mb-5 max-w-xl">
                A curated collection of products I personally use, recommend, and keep coming back to for business, productivity, technology, travel, lifestyle, wellness, and everyday essentials.
              </p>
              <p className="text-matte/70 leading-relaxed max-w-2xl">
                From my favorite office setup and tech tools to travel essentials, wellness products, and everyday finds, this is where I share products I genuinely use and recommend. Everything is organized into curated collections to make it easy to shop the items featured across my content.
              </p>

              <div className="mt-8">
                <span className="btn-primary" aria-hidden="true">
                  Shop My Amazon Store
                  <ArrowUpRight size={14} strokeWidth={1.6} />
                </span>
              </div>
            </a>

            {RESOURCES_ITEMS.map((r, i) => {
              const inner = (
                <>
                  <div className="flex items-start justify-between mb-8">
                    <div className="eyebrow text-taupe">{String(i + 1).padStart(2, '0')}</div>
                    <ArrowUpRight size={20} strokeWidth={1.2} className="text-matte/40 group-hover:text-gold transition-colors duration-500" />
                  </div>
                  <h3 className="font-serif-display text-3xl md:text-4xl tracking-tighter text-matte leading-tight mb-4">{r.label}</h3>
                  <p className="text-matte/70 leading-relaxed">{r.body}</p>
                </>
              );
              const base = 'group block bg-ivory rounded-2xl p-10 border border-transparent hover:border-gold/40 transition-[border-color,transform] duration-500 hover:-translate-y-1';
              if (r.url && r.url.startsWith('/')) {
                return (
                  <Link key={r.key} to={r.url} className={base} data-testid={`resource-card-${r.key}`}>
                    {inner}
                  </Link>
                );
              }
              if (r.url) {
                return (
                  <a
                    key={r.key}
                    href={r.url}
                    target="_blank"
                    rel="noreferrer"
                    className={base}
                    data-testid={`resource-card-${r.key}`}
                  >
                    {inner}
                  </a>
                );
              }
              return (
                <article key={r.key} className={base}>{inner}</article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Digital Products (Store) */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6">
            <PageHero
              src={PHOTOS.resourcesPage}
              alt="Toia Wallace editorial"
              mobileAnchor="top"
              desktopAnchor="center"
              desktopHeight="md:h-[70vh]"
              testId="resources-hero"
            />
          </div>
          <div className="lg:col-span-6">
            <SectionMarker number="04" label="Digital Products" />
            <h2 className="mt-8 font-serif-display text-5xl md:text-6xl tracking-tighter leading-[0.95] text-matte">
              Curated products.
              <br />
              <em className="italic text-taupe">Delivered instantly.</em>
            </h2>
            <p className="mt-8 text-matte/70 leading-relaxed max-w-lg">
              Digital templates, guides, and founder resources. Purchase and delivery happen
              seamlessly, without ever leaving the experience.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to={DIGITAL_PRODUCTS_URL}
                data-testid={RESOURCES.stanStore}
                className="btn-primary"
              >
                Visit Our Store
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </Link>
              <a href={STAN_STRATEGY_URL} target="_blank" rel="noreferrer" className="btn-ghost" data-testid={RESOURCES.strategyCta}>
                Book a Strategy Intensive
                <ArrowRight size={14} strokeWidth={1.6} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter reminder */}
      <section className="py-24 lg:py-32 bg-matte text-ivory">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <div className="eyebrow text-gold mb-8">The Journal</div>
          <h2 className="font-serif-display text-4xl md:text-6xl tracking-tighter leading-tight">
            Subscribe for founder dispatches.
          </h2>
          <p className="mt-6 text-ivory/70 max-w-xl mx-auto">
            A quiet monthly letter on building, deploying AI, and scaling with intention.
          </p>
          <div className="mt-10 flex justify-center">
            <Link
              to="/founders-list"
              data-testid="resources-newsletter-subscribe"
              className="btn-primary bg-gold text-matte hover:bg-[#b8944e]"
            >
              Subscribe
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
